import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { apiWithAuth } from '../../lib/api';
import toast from 'react-hot-toast';
import {
    ArrowLeftIcon,
    CheckCircleIcon,
    XCircleIcon,
    ClockIcon,
    BanknotesIcon,
    ArrowPathIcon
} from '@heroicons/react/24/outline';

const AdminUserWithdrawals = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [processingId, setProcessingId] = useState(null);

    useEffect(() => {
        // Basic Admin Check (can be enhanced with AdminRoute)
        const isAdmin =
            localStorage.getItem('admin_token') ||
            (user && user.user_type === 'admin');

        if (!isAdmin) {
            toast.error('アクセス権限がありません');
            navigate('/');
            return;
        }

        fetchRequests();
    }, [user, navigate]);

    const fetchRequests = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
            const client = apiWithAuth(token);

            const response = await client.get('/admin/withdrawal-requests');
            setRequests(response.data || []);
        } catch (error) {
            console.error('Error fetching withdrawal requests:', error);
            toast.error('出金リクエストの取得に失敗しました');
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id) => {
        if (!window.confirm('この出金リクエストを承認しますか？')) return;

        try {
            setProcessingId(id);
            const token = localStorage.getItem('admin_token') || localStorage.getItem('token');
            const client = apiWithAuth(token);

            await client.post(`/admin/withdrawal-requests/${id}/approve`);

            toast.success('出金リクエストを承認しました');
            // Refresh list
            await fetchRequests();
        } catch (error) {
            console.error('Error approving request:', error);
            toast.error('承認処理に失敗しました');
        } finally {
            setProcessingId(null);
        }
    };

    const StatusBadge = ({ status }) => {
        switch (status) {
            case 'completed':
            case 'approved':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="w-3 h-3 mr-1" />
                        完了
                    </span>
                );
            case 'pending':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <ClockIcon className="w-3 h-3 mr-1" />
                        申請中
                    </span>
                );
            case 'rejected':
            case 'failed':
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        <XCircleIcon className="w-3 h-3 mr-1" />
                        却下/失敗
                    </span>
                );
            default:
                return (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {status}
                    </span>
                );
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center">
                            <button
                                onClick={() => navigate('/admin')}
                                className="mr-4 p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                            >
                                <ArrowLeftIcon className="w-5 h-5" />
                            </button>
                            <h1 className="text-xl font-bold text-gray-900 flex items-center">
                                <BanknotesIcon className="w-6 h-6 mr-2 text-blue-600" />
                                求職者出金リクエスト管理
                            </h1>
                        </div>
                        <button
                            onClick={fetchRequests}
                            className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                            title="更新"
                        >
                            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="bg-white overflow-hidden shadow-sm sm:rounded-lg">
                    {loading && requests.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            読み込み中...
                        </div>
                    ) : requests.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">
                            出金リクエストはありません
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            申請ID / 日時
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            ユーザー
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            金額
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            振込先口座
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            ステータス
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            操作
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {requests.map((req) => (
                                        <tr key={req.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                <div className="font-mono text-xs">{req.id}</div>
                                                <div className="text-xs text-gray-400">
                                                    {req.created_at ? new Date(req.created_at).toLocaleString('ja-JP') : '-'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-medium text-gray-900">
                                                    {req.user_id}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-gray-900">
                                                    ¥{req.amount?.toLocaleString()}
                                                </div>
                                                <div className="text-xs text-gray-500">
                                                    - 手数料: ¥{req.fee?.toLocaleString() || 0}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {req.bank_account ? (
                                                    <div>
                                                        <div className="font-medium">{req.bank_account.bank_name}</div>
                                                        <div className="text-xs">
                                                            {req.bank_account.branch_name} / {req.bank_account.account_type} <br />
                                                            {req.bank_account.account_number}
                                                        </div>
                                                        <div className="text-xs mt-1 bg-gray-100 inline-block px-1 rounded">
                                                            {req.bank_account.account_holder_name}
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <span className="text-red-500">口座情報なし</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <StatusBadge status={req.status} />
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                {req.status === 'pending' && (
                                                    <button
                                                        onClick={() => handleApprove(req.id)}
                                                        disabled={processingId === req.id}
                                                        className="bg-blue-600 text-white px-3 py-1.5 rounded text-xs font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                                    >
                                                        {processingId === req.id ? '処理中...' : '承認する'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AdminUserWithdrawals;
